import { Component, inject, ChangeDetectionStrategy, signal, computed } from '@angular/core';
import { Router, RouterModule } from '@angular/router';

import { MatToolbarModule } from '@angular/material/toolbar';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatChipsModule } from '@angular/material/chips';
import { PermissionService } from '../../services/permission.service';
import { AuthService } from '../../services/auth.service';
import { ImpersonationContextService } from '../../services/impersonation-context.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { GROUP_DISPLAY_NAMES } from '../../user-management/role-config';

@Component({
  selector: 'app-header',
  imports: [MatToolbarModule, MatButtonModule, MatIconModule, MatMenuModule, MatDividerModule, MatTooltipModule, MatChipsModule, RouterModule],
  templateUrl: './header.html',
  styleUrl: './header.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class Header {
  private readonly permissionService = inject(PermissionService);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly impersonationContext = inject(ImpersonationContextService);

  // Auth service observables as signals
  readonly authUserInfo = toSignal(this.authService.userInfo$);
  readonly isAuthenticated = toSignal(this.authService.isAuthenticated$);
  readonly sessionDegraded = toSignal(this.authService.sessionDegraded$, { initialValue: false });

  // Permission service signals
  readonly selectedPermission = this.permissionService.selectedPermission;
  readonly selectedAcademicOfficer = this.permissionService.selectedAcademicOfficer;
  readonly displayPermissionLabel = this.permissionService.displayPermissionLabel;
  readonly displayEmpId = this.permissionService.displayEmpId;
  readonly displayEmail = this.permissionService.displayEmail;

  // API-fetched roles
  readonly categorizedRoles = this.permissionService.categorizedRoles;
  readonly roleSummary = this.permissionService.roleSummary;
  readonly rolesLoading = this.permissionService.rolesLoading;
  readonly apiUserRoles = this.permissionService.apiUserRoles;
  readonly groupDisplayNames = GROUP_DISPLAY_NAMES;

  /**
   * Hides the "View As" menu item for roles with no impersonation authority
   * (Vice Provost for Faculty Affairs, academic officers). Reads assigned roles,
   * so it stays true while already impersonating — otherwise the user could not
   * switch targets.
   */
  readonly showImpersonateItem = computed(() => this.permissionService.canImpersonate());

  /**
   * Identifier for this user's own record in user management. Accounts are keyed
   * on the email, which is what the OIDC `email`/`sub` claim carries.
   */
  readonly myAccountId = computed(() => {
    const info = this.authUserInfo()?.info;
    return info?.email || info?.sub || '';
  });

  /** The user-management detail route is admin-only, so only link it for admins. */
  readonly canViewOwnUserDetails = computed(() =>
    !!this.myAccountId() && this.permissionService.hasAssignedRole('academic_appoint_admin')
  );

  /**
   * Tier 2/3 rows show the scope (reporting unit / department) in their chips, so
   * the row label carries the role name. Derived from GROUP_DISPLAY_NAMES so these
   * stay in step with the dropdowns in user management.
   */
  readonly tier2Label = computed(() => {
    const first = this.categorizedRoles().tier2[0];
    return (first && GROUP_DISPLAY_NAMES[first.group_name]) || 'School/Institute';
  });

  readonly tier3Label = computed(() => {
    const first = this.categorizedRoles().tier3[0];
    return (first && GROUP_DISPLAY_NAMES[first.group_name]) || 'Department';
  });

  // Always show the logged-in user's subject (email)
  readonly loggedInUserSubject = computed(() => {
    const userInfo = this.authUserInfo();
    return userInfo?.info?.sub || '';
  });

  // Always show the logged-in user's name (fallback when userinfo unavailable)
  readonly loggedInUserName = computed(() => {
    const userInfo = this.authUserInfo();
    return userInfo?.info?.name || userInfo?.info?.preferred_username || userInfo?.info?.sub || 'Logged In';
  });

  // Impersonation state (officer / scope / group) — single source of truth.
  readonly isImpersonating = this.permissionService.isImpersonating;

  // Human-readable label for whoever is being impersonated.
  readonly impersonatedUserName = computed(() => this.impersonationContext.label());

  reAuthenticate(): void {
    this.authService.login();
  }

  changePermission(): void {
    this.permissionService.changePermission();
  }

  impersonate(): void {
    this.router.navigate(['/impersonate']);
  }

  redirectToDashboard(): void {
    this.permissionService.redirectToDashboard();
  }

  signOut(): void {
    this.permissionService.signOut();
  }

  stopImpersonating(): void {
    this.permissionService.stopImpersonating();
  }
}
